import CountUp from "react-countup";
import { Props_SnippetStatistic } from "./Statistic.props";
import { CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'
import { useId } from 'react';

export const parseStatConfig = (str: string) => {
    const stringToParse = str.trim();
    const decimalMatch = stringToParse.match(/([.,])(\d+)$/); 
    const integerPart = decimalMatch ? stringToParse.slice(0, -decimalMatch[0].length) : stringToParse;
    const separator = integerPart.match(/[ .,]/)?.[0] || '';
    const parsedValue = parseFloat(integerPart.replace(/\D/g, '') + (decimalMatch ? `.${decimalMatch[2]}` : ''));

    return {
        isNumeric: !isNaN(parsedValue) && /\d/.test(stringToParse),
        end: parsedValue || 0,
        decimals: decimalMatch ? decimalMatch[2].length : 0,
        decimal: decimalMatch ? decimalMatch[1] : ',',
        separator: separator 
    };
};

const Statistic = (props: Props_SnippetStatistic ) => {
    const snippetId = useId();

    const {
        heading,
        content,
        animation,
        backgroundImage,
        repeatable_statisticItems: items = [],
    } = props;

    const animationEnabled = animation?.enabled?.value ?? true;

    const headingClasses = `snippet-statistic__heading ${heading?.visibility.value ? '' : 'snippet-statistic__heading--hidden'}`;
    
    const bgImage = backgroundImage?.src?.value;

    const baseDelay = 0.3;
    const delayIncrement = 0.15;

    const processedItems = items.map((item, index) => {
        const { value, prefix, suffix, description } = item;
        const rawValue = value?.value ?? '';
        const config = parseStatConfig(rawValue);
        const delay = baseDelay + (index * delayIncrement);
        return {
            key: `snippet-statistic-${snippetId}-item-${index}`,
            rawValue,
            config,
            delay,
            prefix,
            suffix,
            description,
        };
    });

    return (
        <section className="snippet-statistic">  
            <img width={550} height={550} src={bgImage} alt="" className="snippet-statistic__background-image" aria-hidden="true" loading="lazy" />
            <div className="snippet-statistic__container">
                <div className="snippet-statistic__container-heading">
                    <h2 className={headingClasses}>
                        {heading?.text?.value}
                    </h2>
                    {content?.text?.value && (
                        <div className="snippet-statistic__description-text">
                            {CompileMarkdown(content.text.value)}
                        </div>
                    )}
                </div>

                <ul className="snippet-statistic__container-items">
                    {processedItems.map(({key, rawValue, config, delay, prefix, suffix, description}) => (
                        <li key={key} className="snippet-statistic__item">
                            <span
                                className="snippet-statistic__item-value"
                                aria-hidden="true"
                            >
                                {animationEnabled && config.isNumeric ? (
                                    <CountUp
                                        start={0}
                                        end={config.end}
                                        duration={2}
                                        delay={delay}
                                        decimals={config.decimals}
                                        decimal={config.decimal}
                                        separator={config.separator}
                                        prefix={prefix?.value}
                                        suffix={suffix?.value}
                                        enableScrollSpy
                                        scrollSpyOnce
                                        scrollSpyDelay={delay * 1000}
                                    >
                                        {({ countUpRef }) => <span ref={countUpRef} />}
                                    </CountUp>
                                ) : (
                                    <>
                                        {prefix?.value}{rawValue}{suffix?.value}
                                    </>
                                )}
                            </span>
                            <span className="snippet-statistic__item-value--sr">
                                {prefix?.value}{rawValue}{suffix?.value}
                            </span>

                            {description?.value && (
                                <span className="snippet-statistic__item-description">
                                    {CompileMarkdown(description.value)}
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
};

export default Statistic;